"use client";

import type { CSSProperties } from "react";
import { Tooltip } from "@mui/material";
import Link from "next/link";
import { TableRow, TableCell, TableActionCell, TableLink, Tag, Avatar } from "@/ui";
import { Mail, Phone } from "lucide-react";
import ConfirmDialog from "@components/shared/ConfirmDialog";
import DateTime from "@components/shared/DateTime";
import { JobStatus } from "@lib/enums";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import isPlaceholderEmail from "@lib/candidates/isPlaceholderEmail";
import isPlaceholderPhoto from "@lib/candidates/isPlaceholderPhoto";
import useDeleteCandidate from "@lib/candidates/useDeleteCandidate";

const iconBtn: CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 32, height: 32, border: "none", background: "transparent",
  borderRadius: "var(--radius-md)", cursor: "pointer",
  color: "var(--action-primary)", padding: 0,
  transition: "background-color 150ms ease, color 150ms ease",
};

interface CandidateSkill {
  id: number;
  name: string;
}

interface Candidate {
  id: number;
  uuid: string;
  name: string;
  photo_url?: string | null;
  position_title?: string | null;
  skills?: CandidateSkill[];
  updated_at: string;
  email?: string | null;
  phone?: string | null;
}

interface CandidateRowProps {
  candidate: Candidate;
  openProfile: (candidate: Candidate) => void;
  status?: string;
}

export default function CandidateRow({ candidate, openProfile, status }: CandidateRowProps) {
  const [rowClass, setRowClass] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutate } = useDeleteCandidate();
  const deleteCandidate = mutate as unknown as (candidateId: string) => void;
  const router = useRouter();

  useEffect(() => {
    if (status === JobStatus.COMPLETE) {
      const timer = setTimeout(() => setRowClass("success"), 200);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const handleEmailClick = () => {
    if (!isPlaceholderEmail(candidate.email)) {
      navigator.clipboard.writeText(candidate.email);
      toast("Email copied to clipboard");
    }
  };

  const handlePhoneClick = () => {
    navigator.clipboard.writeText(candidate.phone);
    toast("Phone number copied to clipboard");
  };

  const email = isPlaceholderEmail(candidate.email) ? "" : candidate.email;

  const actionItems = [
    { label: "Edit", onClick: () => openProfile(candidate) },
    { label: "Float candidate", onClick: () => router.push(`/floats/create?candidates[]=${candidate.id}`) },
    { label: "Submit candidate", onClick: () => router.push(`/submissions/create?candidates[]=${candidate.id}`) },
    { label: "Delete", onClick: () => setConfirmOpen(true), destructive: true },
  ];

  return (
    <>
      <TableRow className={rowClass}>
        <TableCell>
          <Avatar
            src={isPlaceholderPhoto(candidate.photo_url) ? undefined : candidate.photo_url}
            name={candidate.name}
            size="sm"
          />
        </TableCell>
        <TableCell>
          <div>
            <TableLink
              component={Link}
              href={`/candidates/${candidate.uuid}`}
              onClick={(event) => {
                event.preventDefault();
                openProfile(candidate);
              }}
            >
              {candidate.name}
            </TableLink>
            {candidate.position_title && (
              <div style={{ fontSize: 12, color: "var(--text-secondary, #6b7280)", marginTop: 2 }}>{candidate.position_title}</div>
            )}
          </div>
        </TableCell>
        <TableCell>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {candidate.skills?.slice(0, 3).map((skill) => (
              <Tag key={skill.id} variant="skill">{skill.name}</Tag>
            ))}
            {candidate.skills?.length > 3 && (
              <Tooltip title={candidate.skills.slice(3).map((s) => s.name).join(", ")} placement="top" arrow>
                <span><Tag variant="minimalOutline">+{candidate.skills.length - 3} more</Tag></span>
              </Tooltip>
            )}
          </div>
        </TableCell>
        <TableCell>
          <DateTime date={candidate.updated_at} />
        </TableCell>
        <TableCell>
          <div style={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            {email && (
              <Tooltip title={email} placement="top" arrow>
                <button onClick={handleEmailClick} style={iconBtn}>
                  <Mail size={16} />
                </button>
              </Tooltip>
            )}
            {candidate.phone && (
              <Tooltip title={candidate.phone} placement="top" arrow>
                <button onClick={handlePhoneClick} style={iconBtn}>
                  <Phone size={16} />
                </button>
              </Tooltip>
            )}
          </div>
        </TableCell>
        <TableActionCell items={actionItems} />
      </TableRow>
      <ConfirmDialog
        title="Delete candidate"
        message="Are you sure you want to delete this candidate?"
        open={confirmOpen}
        onConfirm={() => {
          setConfirmOpen(false);
          deleteCandidate(candidate.uuid);
          toast.success("Person deleted");
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
